import { useStyles, Flex, Button, pxToRem, Grid } from '@archipro-design/aria';
import { cn } from '@rocco/utils/cn';
import { InPageSearchBar } from '@modules/search/components/in-page-search-bar/InPageSearchBar';
import * as S from './SearchFilter.style';
import { useProfessionalTabSearch } from '~/modules/professional/hook/use-professional-tab-search';
import { useCurrentTabPath } from '~/modules/professional/hook/use-current-tab-path';
import SearchSort from '~/modules/search/components/search-filter-section/RoccoBasedSearchSort';
import { Filter } from '@archipro-design/icons';
import { applyCount } from '@modules/search/components/filter-drawer/FilterDrawer';
import FilterBarFilters from '~/modules/search/components/search-filter-section/FilterBarFilters';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { useSearchSortOptions } from '~/modules/search/hook/use-search-sort-options';
import type { SearchFilterProp } from './SearchFilter';
import { useSortControlsEnabled } from '@modules/search/config/sort-controls';

/**
 * Mirrors the browse pill bar's filter toggle so the professional tabs read as
 * the same control. Kept in sync by hand with `filterPillClassName` in
 * `ui-rocco/pages/directory-browse/DirectoryPillBar.tsx`.
 */
const filterPillClassName = (isDark: boolean) =>
    cn(
        'flex h-10.5 shrink-0 items-center gap-2 rounded-full px-[1.25rem] text-[1rem] tracking-[-0.05rem] transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        isDark
            ? 'bg-[#d5d5d5] text-[#181818] hover:bg-[#c8c8c8] active:bg-[#bcbcbc]'
            : 'bg-[#181818] text-white hover:bg-[#2a2a2a] active:bg-[#333333]',
    );

const FilterPillIcon = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        aria-hidden="true"
        className="shrink-0"
    >
        <path
            d="M1.5 3.75h12M3.5 7.5h8M5.5 11.25h4"
            stroke="currentColor"
            strokeWidth="1.3"
            strokeLinecap="round"
        />
    </svg>
);

/** Applied-facet count — the only indicator that filters are on once the drawer closes. */
const FilterPillCount = ({
    count,
    isDark,
}: {
    count: number;
    isDark: boolean;
}) => {
    if (count <= 0) return null;
    return (
        <span
            className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full text-[0.6875rem] font-medium',
                isDark ? 'bg-[#181818] text-white' : 'bg-white text-[#181818]',
            )}
        >
            {count}
        </span>
    );
};

export const SearchFilterDesktop: React.FC<SearchFilterProp> = ({
    searchFilters,
    toggleFilterDrawer,
    filterDrawerOpen,
    disableSort,
    disableToggleFilter,
    searchBarVariant,
    searchBarTheme,
    searchPlaceholder,
    displayControl,
}) => {
    const currentTabPath = useCurrentTabPath();
    const { css } = useStyles({ currentTabPath });
    const tabSearch = useProfessionalTabSearch();
    const category = useMatchesHandleData<SearchIndex>('category', 'project');
    const isAllowFilters = useMatchesHandleData<boolean>('allowFilters', true);
    const { appliedFilters, filterBarFilters, updateFilter } = searchFilters;

    const dropdownOptionResult = useSearchSortOptions({
        category,
        sortBy: searchFilters.filterStates.sortBy,
        defaultSortBy: searchFilters.filterStates.defaultSortBy,
    });
    const sortControlsEnabled = useSortControlsEnabled();

    // The revamp tabs mirror the browse pill bar: filter toggle on the left,
    // search and sort on the right. Built with plain Tailwind rather than the
    // aria Flex/Grid below — that wrapper cascades a smaller font onto the
    // Rocco sort button, which is what made these controls read two pixels
    // smaller than the same component on the browse pages.
    if (searchBarVariant === 'revamp') {
        const isDark = searchBarTheme === 'dark';
        // Tabs without filters (Articles) have nothing to put on the left, and
        // pushing the search pill to the far right left them out of step with
        // Videos and Downloads, which keep search left via ProfessionalItemsBar.
        // Anchor search left when it would otherwise be the only thing in the
        // row.
        const hasFilterControls = isAllowFilters && !disableToggleFilter;
        const searchBar = (
            <InPageSearchBar
                {...tabSearch}
                variant={searchBarVariant}
                theme={searchBarTheme}
                placeholder={searchPlaceholder}
            />
        );
        return (
            <div className="flex items-center justify-between gap-6">
                <div className="flex min-w-0 items-center gap-4">
                    {!hasFilterControls && searchBar}
                    {isAllowFilters && !disableToggleFilter && (
                        <button
                            type="button"
                            onClick={toggleFilterDrawer}
                            aria-pressed={filterDrawerOpen}
                            disabled={disableSort}
                            className={filterPillClassName(isDark)}
                        >
                            <FilterPillIcon />
                            {filterDrawerOpen ? 'Hide Filters' : 'Show Filters'}
                            <FilterPillCount
                                count={appliedFilters?.length ?? 0}
                                isDark={isDark}
                            />
                        </button>
                    )}
                    {isAllowFilters &&
                        filterBarFilters &&
                        filterBarFilters.length > 0 && (
                            <div
                                className={cn(
                                    'flex min-w-0 items-center',
                                    // Scoped to the unthemed controls: the filter
                                    // pill above is self-themed and the search bar
                                    // themes itself, so neither wants this.
                                    isDark &&
                                        '[&_*]:text-white! [&_svg]:fill-white! [&_svg]:text-white!',
                                )}
                            >
                                <FilterBarFilters
                                    filters={filterBarFilters}
                                    updateFilter={updateFilter}
                                />
                            </div>
                        )}
                </div>
                {/* min-w-0 (not shrink-0): when the sidebar narrows the row the
                    search pill gives way first — the parent sections clip
                    overflow rather than reflowing, so a rigid cluster would
                    push Display/Sort out of view. */}
                <div className="flex min-w-0 items-center gap-6">
                    {hasFilterControls && searchBar}
                    {(displayControl ||
                        (!disableSort &&
                            dropdownOptionResult.items.length > 0)) && (
                        <div
                            className={cn(
                                'flex shrink-0 items-center gap-6',
                                isDark &&
                                    '[&_*]:text-white! [&_svg]:fill-white! [&_svg]:text-white!',
                            )}
                        >
                            {displayControl}
                            {sortControlsEnabled &&
                                !disableSort &&
                                dropdownOptionResult.items.length > 0 && (
                                    <SearchSort
                                        optionResult={dropdownOptionResult}
                                        preventScrollReset
                                    />
                                )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={css(S.DesktopFilterWarp)}>
            <Flex space={'between'}>
                <div className={css(S.SearchBarWarp)}>
                    <InPageSearchBar
                        {...tabSearch}
                        variant={searchBarVariant}
                        theme={searchBarTheme}
                        placeholder={searchPlaceholder}
                    />
                </div>
                <Grid
                    className={cn(
                        css(S.DesktopFilterGroup),
                        // Dark Projects tab: white the filters/sort here (scoped
                        // to this group) instead of wrapping the whole bar, so
                        // the self-themed revamp search pill is left alone.
                        searchBarTheme === 'dark' &&
                            '[&_*]:text-white! [&_::placeholder]:text-white/60! [&_svg]:fill-white! [&_svg]:text-white!',
                    )}
                    columns={
                        disableToggleFilter
                            ? 'repeat(2, auto)'
                            : 'repeat(3, auto)'
                    }
                    rows={1}
                >
                    <div>
                        {isAllowFilters && (
                            <Flex
                                className={css(S.FilterButtonsWarp)}
                                vAlign={'center'}
                            >
                                {filterBarFilters &&
                                    filterBarFilters.length > 0 && (
                                        <FilterBarFilters
                                            filters={filterBarFilters}
                                            updateFilter={updateFilter}
                                        />
                                    )}
                            </Flex>
                        )}
                    </div>

                    {isAllowFilters && !disableToggleFilter && (
                        <Flex
                            className={css(S.FilterButtonsWarp)}
                            vAlign={'center'}
                        >
                            <Button
                                transparent
                                size={18}
                                variables={{
                                    buttonPadding: 0,
                                    iconPadding: 0,
                                    buttonSize18IconXMargin: pxToRem(12),
                                }}
                                disabled={disableSort}
                                iconBefore={<Filter />}
                                onClick={toggleFilterDrawer}
                                styles={{
                                    fontWeight: 500,
                                }}
                            >
                                {applyCount(
                                    `${
                                        filterDrawerOpen ? 'Hide' : 'Show'
                                    } Filters`,
                                    appliedFilters?.length ?? 0,
                                )}
                            </Button>
                        </Flex>
                    )}

                    <div className={css(S.SortButtonWarp)}>
                        {sortControlsEnabled &&
                            !disableSort &&
                            dropdownOptionResult.items.length > 0 && (
                                <SearchSort
                                    optionResult={dropdownOptionResult}
                                    // Only the revamp tabs hold their scroll
                                    // position on sort, and they return above;
                                    // the legacy tabs keep the scroll-to-top
                                    // they have always had.
                                    preventScrollReset={false}
                                />
                            )}
                    </div>
                </Grid>
            </Flex>
        </div>
    );
};
